import { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import styled from 'styled-components';

import CheckboxDefault from 'app/components/Checkbox/CheckboxDefault';
import Typography from 'app/components/Typography';

import VisualArchiveInputDateFilter from 'app/modules/system-settings/devices/components/VisualArchiveInputDateFilter';

import { endOfDay, startOfDay } from 'date-fns';

import { VISUAL_ARCHIVE_NAME_FILTER_OPTIONS } from '../core/constant';
import { VisualArchiveFilterParams } from '../core/type';

export const Main = styled.div`
  width: 600px;
  display: flex;
  flex-direction: column;
`;

export const Header = styled.div`
  height: 80px;
  padding: 0px 28px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid var(--neutral-3);

  .clear_all:hover {
    text-decoration: underline;
  }
`;

export const Body = styled.div`
  padding: 28px;
  display: flex;
  flex-direction: column;
`;

interface Props {
  onApplyFilter: (params: VisualArchiveFilterParams) => void;
  currentFilter?: VisualArchiveFilterParams;
}

const VisualArchiveFilter: React.FC<Props> = ({ onApplyFilter, currentFilter = {} }) => {
  const [fromDate, setFromDate] = useState<Date | null>(currentFilter.fromDate || null);
  const [toDate, setToDate] = useState<Date | null>(currentFilter.toDate || null);
  const [nameFilters, setNameFilters] = useState<string[]>(currentFilter.nameFilters || []);

  useEffect(() => {
    if (currentFilter.fromDate) {
      setFromDate(currentFilter.fromDate);
    }
    if (currentFilter.toDate) {
      setToDate(currentFilter.toDate);
    }
    setNameFilters(currentFilter.nameFilters || []);
  }, [currentFilter]);

  const handleToggleNameFilter = (value: string) => () => {
    setNameFilters((prev) =>
      prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value],
    );
  };

  const handleApplyFilter = () => {
    const filterParams: VisualArchiveFilterParams = {};

    if (fromDate) {
      filterParams.fromDate = startOfDay(fromDate);
    }
    if (toDate) {
      filterParams.toDate = endOfDay(toDate);
    }
    if (nameFilters.length > 0) {
      filterParams.nameFilters = nameFilters;
    }

    onApplyFilter(filterParams);
  };

  const handleClearAll = () => {
    setFromDate(null);
    setToDate(null);
    setNameFilters([]);
    onApplyFilter({});
  };

  const handleOnChangeDate = (newDate: any) => {
    if (!newDate || newDate.length === 0) {
      return;
    }
    const { startDate, endDate } = newDate[0];
    setFromDate(startDate ? new Date(startDate) : null);
    setToDate(endDate ? new Date(endDate) : null);
  };

  const handleClearDate = () => {
    setFromDate(null);
    setToDate(null);
  };

  const labelFilterDate = (from: Date | null, to: Date | null) => {
    if (!from && !to) return 'Select date range';
    if (from && !to) return `From ${from.toLocaleDateString('en-US')}`;
    if (!from && to) return `To ${to.toLocaleDateString('en-US')}`;
    return `${from?.toLocaleDateString('en-US')} - ${to?.toLocaleDateString('en-US')}`;
  };

  return (
    <Main>
      <Header>
        <Typography variant="heading-6">Filter</Typography>
        <Typography
          className="clear_all cursor-pointer"
          variant="body-5"
          onClick={handleClearAll}
          color="primary"
        >
          Clear all
        </Typography>
      </Header>
      <Body>
        <VisualArchiveInputDateFilter
          label="Range Date"
          value={labelFilterDate(fromDate, toDate)}
          onChange={handleOnChangeDate}
          state={[
            {
              startDate: fromDate || new Date(),
              endDate: toDate || new Date(),
              key: 'selection',
            },
          ]}
          onClear={handleClearDate}
        />

        <div className="mt-4">
          <Typography variant="body-7" color="gray-700" className="mb-2">
            Name
          </Typography>
          <div className="d-flex flex-column gap-2">
            {VISUAL_ARCHIVE_NAME_FILTER_OPTIONS.map((option) => (
              <CheckboxDefault
                key={option.value}
                checked={nameFilters.includes(option.value)}
                onChange={handleToggleNameFilter(option.value)}
                label={option.label}
              />
            ))}
          </div>
        </div>

        <div className="d-flex justify-content-end mt-4 gap-2">
          <Button variant="primary" onClick={handleApplyFilter}>
            Apply
          </Button>
        </div>
      </Body>
    </Main>
  );
};

export default VisualArchiveFilter;
